import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, api } from '../lib/api.js';

// ─── Local Types ──────────────────────────────────────────────

interface OnboardingRecord {
  id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  department?: string;
  dark_web_monitor_added: boolean;
  dark_web_monitor_id?: string;
  training_assigned: boolean;
  training_campaign_ids: string[];
  status: 'in_progress' | 'completed' | 'overdue';
  started_at: string;
  due_date: string;
  completed_at?: string;
  overdue_alerted: boolean;
  trigger_source: string;
  created_at: string;
  campaigns?: TrainingCampaign[];
}

interface TrainingCampaign {
  id: string;
  title: string;
  module_type: string;
  duration_minutes?: number;
  passing_score?: number;
  is_required: boolean;
  status: 'assigned' | 'in_progress' | 'completed';
  due_date: string;
  completed_at?: string;
}

interface OnboardingModule {
  title: string;
  type: 'course' | 'video' | 'quiz' | 'policy_acknowledgement';
  duration_minutes?: number;
  passing_score?: number;
  is_required: boolean;
}

interface OnboardingConfig {
  id: string;
  auto_add_dark_web_monitor: boolean;
  training_modules: OnboardingModule[];
  onboarding_due_days: number;
  send_welcome_email: boolean;
  welcome_email_template?: string;
}

interface RecordsResponse {
  data: OnboardingRecord[];
  hasMore?: boolean;
}

interface ConfigResponse {
  data: OnboardingConfig;
}

interface SummaryResponse {
  data: {
    in_progress: number;
    completed: number;
    overdue: number;
    avg_completion_days: number | null;
  };
}

interface SingleRecordResponse {
  data: OnboardingRecord & { campaigns?: TrainingCampaign[] };
}

// ─── Status badge helper ──────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors =
    status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
    status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
    status === 'overdue' ? 'bg-red-100 text-red-700' :
    'bg-gray-100 text-gray-600';
  const label =
    status === 'in_progress' ? 'In Progress' :
    status === 'completed' ? 'Completed' :
    status === 'overdue' ? 'Overdue' :
    status;
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors}`}>{label}</span>;
}

// ─── Page ─────────────────────────────────────────────────────

export function UserOnboardingPage() {
  const { data: recordsData, loading, error, refresh } = useApi<RecordsResponse>('/protect/onboarding');
  const { data: configData, refresh: refreshConfig } = useApi<ConfigResponse>('/protect/onboarding?action=config');
  const { data: summaryData, refresh: refreshSummary } = useApi<SummaryResponse>('/protect/onboarding?action=summary');

  // Config panel
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState<Partial<OnboardingConfig> | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // Onboard user modal
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardEmail, setOnboardEmail] = useState('');
  const [onboardName, setOnboardName] = useState('');
  const [onboardDept, setOnboardDept] = useState('');
  const [onboarding, setOnboarding] = useState(false);

  // Bulk import modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Detail slide-out
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data: detailData, loading: detailLoading } = useApi<SingleRecordResponse>(
    detailId ? `/protect/onboarding/${detailId}` : null,
  );

  const records = recordsData?.data ?? [];
  const config = configData?.data ?? null;
  const summary = summaryData?.data ?? null;

  // Initialise editable config form when config loads or panel opens
  function openConfigPanel() {
    if (config) setConfigForm({ ...config });
    setConfigOpen(true);
  }

  async function handleSaveConfig() {
    if (!configForm) return;
    setSavingConfig(true);
    try {
      await api('/protect/onboarding', {
        method: 'PATCH',
        body: JSON.stringify(configForm),
      });
      refreshConfig();
      setConfigOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleOnboardUser(e: React.FormEvent) {
    e.preventDefault();
    if (!onboardEmail.trim()) return;
    setOnboarding(true);
    try {
      await post('/protect/onboarding?action=trigger', {
        user_email: onboardEmail.trim(),
        user_name: onboardName.trim() || undefined,
        department: onboardDept.trim() || undefined,
      });
      setShowOnboardModal(false);
      setOnboardEmail('');
      setOnboardName('');
      setOnboardDept('');
      refresh();
      refreshSummary();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger onboarding');
    } finally {
      setOnboarding(false);
    }
  }

  async function handleBulkImport(e: React.FormEvent) {
    e.preventDefault();
    setBulkImporting(true);
    try {
      const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
      const users = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return { user_email: parts[0] ?? '', user_name: parts[1], department: parts[2] };
      }).filter(u => u.user_email);

      if (users.length === 0) {
        toast.error('No valid email addresses found');
        return;
      }

      await post('/protect/onboarding?action=bulk_trigger', { users });
      setShowBulkModal(false);
      setBulkText('');
      refresh();
      refreshSummary();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to bulk import');
    } finally {
      setBulkImporting(false);
    }
  }

  async function handleSendReminder(record: OnboardingRecord) {
    try {
      await post('/protect/onboarding?action=trigger', {
        user_email: record.user_email,
        user_name: record.user_name,
        department: record.department,
      });
      toast.success(`Reminder triggered for ${record.user_email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send reminder');
    }
  }

  function addModule() {
    if (!configForm) return;
    setConfigForm({
      ...configForm,
      training_modules: [
        ...(configForm.training_modules ?? []),
        { title: '', type: 'course', duration_minutes: 30, is_required: true },
      ],
    });
  }

  function updateModule(index: number, field: keyof OnboardingModule, value: unknown) {
    if (!configForm?.training_modules) return;
    const mods = [...configForm.training_modules];
    mods[index] = { ...mods[index]!, [field]: value } as OnboardingModule;
    setConfigForm({ ...configForm, training_modules: mods });
  }

  function removeModule(index: number) {
    if (!configForm?.training_modules) return;
    const mods = configForm.training_modules.filter((_, i) => i !== index);
    setConfigForm({ ...configForm, training_modules: mods });
  }

  const detail = detailData?.data ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading user onboarding data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Security Onboarding</h2>
          <p className="text-gray-500 text-sm mt-1">Automatically enroll new employees in security training and dark web monitoring.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBulkModal(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
          >
            Bulk Import
          </button>
          <button
            onClick={() => setShowOnboardModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
          >
            + Onboard User
          </button>
        </div>
      </div>

      {/* Config Panel */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <button
          onClick={() => (configOpen ? setConfigOpen(false) : openConfigPanel())}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <span className="font-semibold text-gray-800">Onboarding Configuration</span>
          <span className="text-gray-400 text-sm">{configOpen ? '▲ Collapse' : '▼ Expand'}</span>
        </button>

        {configOpen && configForm && (
          <div className="border-t border-gray-100 px-5 pb-5">
            <div className="grid grid-cols-2 gap-6 mt-4">
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configForm.auto_add_dark_web_monitor ?? true}
                    onChange={e => setConfigForm({ ...configForm, auto_add_dark_web_monitor: e.target.checked })}
                    className="h-4 w-4 text-emerald-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Auto-add to Dark Web Monitoring</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={configForm.send_welcome_email ?? true}
                    onChange={e => setConfigForm({ ...configForm, send_welcome_email: e.target.checked })}
                    className="h-4 w-4 text-emerald-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Send Welcome Email</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Onboarding Due Days
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={configForm.onboarding_due_days ?? 7}
                    onChange={e => setConfigForm({ ...configForm, onboarding_due_days: parseInt(e.target.value, 10) })}
                    className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Training Modules</span>
                  <button
                    onClick={addModule}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    + Add Module
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(configForm.training_modules ?? []).map((mod, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2">
                      <input
                        type="text"
                        value={mod.title}
                        onChange={e => updateModule(i, 'title', e.target.value)}
                        placeholder="Module title"
                        className="flex-1 text-xs border border-gray-200 rounded px-2 py-1"
                      />
                      <select
                        value={mod.type}
                        onChange={e => updateModule(i, 'type', e.target.value)}
                        className="text-xs border border-gray-200 rounded px-1 py-1"
                      >
                        <option value="course">Course</option>
                        <option value="video">Video</option>
                        <option value="quiz">Quiz</option>
                        <option value="policy_acknowledgement">Policy</option>
                      </select>
                      <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={mod.is_required}
                          onChange={e => updateModule(i, 'is_required', e.target.checked)}
                          className="h-3 w-3"
                        />
                        Required
                      </label>
                      <button
                        onClick={() => removeModule(i)}
                        className="text-red-400 hover:text-red-600 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setConfigOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
              >
                {savingConfig ? 'Saving...' : 'Save Config'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats Row */}
      {summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{summary.in_progress}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.completed}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Overdue</p>
            <p className={`text-2xl font-bold mt-1 ${summary.overdue > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {summary.overdue}
              {summary.overdue > 0 && (
                <span className="ml-2 text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded-full">!</span>
              )}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Avg Completion</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {summary.avg_completion_days != null ? `${summary.avg_completion_days}d` : '—'}
            </p>
          </div>
        </div>
      )}

      {/* Records Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Dept</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Started</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Due</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Steps</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  No onboarding records yet. Click "Onboard User" to get started.
                </td>
              </tr>
            ) : records.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{r.user_email}</div>
                  {r.user_name && <div className="text-xs text-gray-500">{r.user_name}</div>}
                </td>
                <td className="px-4 py-3 text-gray-600">{r.department ?? '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{new Date(r.started_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={r.status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'}>
                    {new Date(r.due_date).toLocaleDateString()}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 space-x-2">
                  <span title="Dark Web Monitor">{r.dark_web_monitor_added ? '🟢 DW' : '⚪ DW'}</span>
                  <span title="Training Assigned">{r.training_assigned ? '🟢 Training' : '⚪ Training'}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setDetailId(r.id)}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Onboard User Modal */}
      {showOnboardModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Onboard User</h3>
            </div>
            <form onSubmit={handleOnboardUser} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={onboardEmail}
                  onChange={e => setOnboardEmail(e.target.value)}
                  placeholder="employee@company.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
                <input
                  type="text"
                  value={onboardName}
                  onChange={e => setOnboardName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={onboardDept}
                  onChange={e => setOnboardDept(e.target.value)}
                  placeholder="Engineering"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowOnboardModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={onboarding}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {onboarding ? 'Starting...' : 'Start Onboarding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Import Users</h3>
            </div>
            <form onSubmit={handleBulkImport} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Users (one per line: email, name, department)
                </label>
                <textarea
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                  placeholder={'alice@company.com, Alice Smith, Engineering\nbob@company.com, Bob Jones, Sales'}
                  rows={8}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">CSV format: email, name (optional), department (optional)</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkImporting}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {bulkImporting ? 'Importing...' : 'Import'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Slide-out */}
      {detailId && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={() => setDetailId(null)} />
          <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Onboarding Detail</h3>
              <button
                onClick={() => setDetailId(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {detailLoading && (
                <div className="text-gray-500 text-sm">Loading...</div>
              )}

              {detail && (
                <div className="space-y-5">
                  {/* User info */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{detail.user_email}</span>
                      <StatusBadge status={detail.status} />
                    </div>
                    {detail.user_name && <div className="text-sm text-gray-600">{detail.user_name}</div>}
                    {detail.department && <div className="text-xs text-gray-500">Department: {detail.department}</div>}
                    <div className="text-xs text-gray-500">
                      Started: {new Date(detail.started_at).toLocaleDateString()} · Due: {new Date(detail.due_date).toLocaleDateString()}
                    </div>
                    {detail.completed_at && (
                      <div className="text-xs text-emerald-600 font-medium">
                        Completed: {new Date(detail.completed_at).toLocaleDateString()}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 capitalize">Source: {detail.trigger_source.replace('_', ' ')}</div>
                  </div>

                  {/* Steps checklist */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Onboarding Steps</h4>
                    <div className="space-y-2">
                      <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${detail.dark_web_monitor_added ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
                        <span>{detail.dark_web_monitor_added ? '✓' : '○'}</span>
                        <span>Dark Web Monitor Added</span>
                      </div>
                      <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${detail.training_assigned ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
                        <span>{detail.training_assigned ? '✓' : '○'}</span>
                        <span>Training Assigned ({detail.training_campaign_ids.length} module{detail.training_campaign_ids.length !== 1 ? 's' : ''})</span>
                      </div>
                    </div>
                  </div>

                  {/* Training campaigns */}
                  {detail.campaigns && detail.campaigns.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Training Modules</h4>
                      <div className="space-y-2">
                        {detail.campaigns.map(c => (
                          <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
                            <div>
                              <span className={`font-medium ${c.status === 'completed' ? 'text-emerald-700' : 'text-gray-800'}`}>
                                {c.status === 'completed' ? '✓ ' : '○ '}
                                {c.title}
                              </span>
                              <div className="text-xs text-gray-400 mt-0.5 capitalize">
                                {c.module_type.replace('_', ' ')}
                                {c.duration_minutes ? ` · ${c.duration_minutes}min` : ''}
                                {c.is_required ? ' · Required' : ' · Optional'}
                              </div>
                            </div>
                            <div>
                              {c.status === 'completed' ? (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Done</span>
                              ) : (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">{c.status}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Overdue reminder */}
                  {(detail.status === 'overdue' || detail.status === 'in_progress') && (
                    <div className="pt-2">
                      <button
                        onClick={() => handleSendReminder(detail)}
                        className="w-full px-4 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium rounded-md hover:bg-amber-100"
                      >
                        Send Reminder
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
